import { z } from 'zod';

import {
  BlockCategory,
  BlockCustomIconVariant,
  BlockLifeCycle,
  BlockSafetyLevel,
} from './blockAttributes.js';
import { AnyBlockTypeSchema, UnknownBlockType } from './flowDefinition.js';
import { oneOrMany } from '../../utils.js';
import { BaseItemSchema, SortOrder } from '../common.js';

export const BaseBlockTypeMetadataSchema = z.object({
  blockType: AnyBlockTypeSchema,
  name: z.string(),
  description: z.string(),
  blockCategory: z.nativeEnum(BlockCategory),
  blockSafetyLevel: z.nativeEnum(BlockSafetyLevel),
  blockLifeCycle: z.nativeEnum(BlockLifeCycle),
  customIconVariant: z.nativeEnum(BlockCustomIconVariant).nullish(),
  searchKeywords: z.array(z.string()).nullish(),
});
export type BaseBlockTypeMetadata = z.infer<typeof BaseBlockTypeMetadataSchema>;

export const BlockTypeMetadataSchema = BaseItemSchema.merge(BaseBlockTypeMetadataSchema);
export type BlockTypeMetadataSchema = z.infer<typeof BlockTypeMetadataSchema>;

export const UnknownBlockTypeMetadata: BaseBlockTypeMetadata = {
  blockType: UnknownBlockType.Unknown,
  name: 'Unknown',
  description: `Unknown block type.
This may be a new block type that is not synchronized yet.
`,
  blockCategory: BlockCategory.Obsolete,
  blockSafetyLevel: BlockSafetyLevel.Unsafe,
  blockLifeCycle: BlockLifeCycle.Retired,
};

export const BlockTypeMetadataFindAllOptionsSchema = z.object({
  q: z.string().optional(),
  blockCategory: oneOrMany(z.nativeEnum(BlockCategory).optional()),
  blockSafetyLevel: oneOrMany(z.nativeEnum(BlockSafetyLevel).optional()),
  blockLifeCycle: oneOrMany(z.nativeEnum(BlockLifeCycle))
    .default([BlockLifeCycle.Published, BlockLifeCycle.Beta])
    .optional(),
  sortModel: z
    .array(
      z.object({
        field: z.enum(['name', 'blockCategory', 'blockSafetyLevel', 'blockLifeCyclePriority']),
        sort: z.nativeEnum(SortOrder),
      })
    )
    .optional(),
  limit: z.coerce.number().int().positive().max(1000).optional(),
  offset: z.coerce.number().int().nonnegative().optional(),
});
export type BlockTypeMetadataFindAllOptions = z.infer<typeof BlockTypeMetadataFindAllOptionsSchema>;

export const UpdateBlockTypeMetadataSchema = z.object({
  name: z.string().trim().min(1).optional(),
  description: z.string().trim().min(1).optional(),
  blockCategory: z.nativeEnum(BlockCategory).optional(),
  blockSafetyLevel: z.nativeEnum(BlockSafetyLevel).optional(),
  blockLifeCycle: z.nativeEnum(BlockLifeCycle).optional(),
  customIconVariant: z.nativeEnum(BlockCustomIconVariant).nullish(),
  searchKeywords: z.array(z.string().trim().min(1)).min(1).nullish(),
});
export type UpdateBlockTypeMetadata = z.infer<typeof UpdateBlockTypeMetadataSchema>;
